import { useState } from 'react'
import type { SummaryInput } from '@creanizer/types'
import { Label } from '@creanizer/ui'
import { Input } from '@creanizer/ui'
import { Slider } from '@creanizer/ui'
import { Switch } from '@creanizer/ui'
import { Button } from '@creanizer/ui'
import { Checkbox } from '@creanizer/ui'
import { ChevronDown, ChevronUp, Settings } from 'lucide-react'

interface SummarySettingsProps {
  input: SummaryInput
  onChange: (input: SummaryInput) => void
}

export function SummarySettings({ input, onChange }: SummarySettingsProps) {
  const [isExpanded, setIsExpanded] = useState(false)
  
  return (
    <div className="space-y-4">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full justify-between"
      >
        <div className="flex items-center gap-2">
          <Settings className="h-4 w-4" />
          <span>詳細設定</span>
        </div>
        {isExpanded ? (
          <ChevronUp className="h-4 w-4" />
        ) : (
          <ChevronDown className="h-4 w-4" />
        )}
      </Button>
      
      {isExpanded && (
        <div className="space-y-4 pl-6">
          {/* 重点キャラクター */}
          <div className="space-y-2">
            <Label htmlFor="focusCharacters">重点キャラクター</Label>
            <Input
              id="focusCharacters"
              value={input.focusCharacters?.join(', ') || ''}
              onChange={(e) => {
                const chars = e.target.value
                  .split(',')
                  .map(s => s.trim())
                  .filter(s => s.length > 0)
                onChange({
                  ...input,
                  focusCharacters: chars.length > 0 ? chars : undefined
                })
              }}
              placeholder="太郎, 花子, 次郎（カンマ区切り）"
            />
            <p className="text-xs text-muted-foreground">
              これらのキャラクターの行動や台詞を優先的に含めます
            </p>
          </div>
          
          {/* 重点テーマ */}
          <div className="space-y-2">
            <Label htmlFor="focusThemes">重点テーマ</Label>
            <Input
              id="focusThemes"
              value={input.focusThemes?.join(', ') || ''}
              onChange={(e) => {
                const themes = e.target.value
                  .split(',')
                  .map(s => s.trim())
                  .filter(s => s.length > 0)
                onChange({
                  ...input,
                  focusThemes: themes.length > 0 ? themes : undefined
                })
              }}
              placeholder="友情, 成長, 冒険（カンマ区切り）"
            />
            <p className="text-xs text-muted-foreground">
              これらのテーマに関連する内容を重視します
            </p>
          </div>
          
          {/* 除外パターン */}
          <div className="space-y-2">
            <Label htmlFor="excludePatterns">除外パターン</Label>
            <Input
              id="excludePatterns"
              value={input.excludePatterns?.join(', ') || ''}
              onChange={(e) => {
                const patterns = e.target.value
                  .split(',')
                  .map(s => s.trim())
                  .filter(s => s.length > 0)
                onChange({
                  ...input,
                  excludePatterns: patterns.length > 0 ? patterns : undefined
                })
              }}
              placeholder="※注釈, 【メモ】（カンマ区切り）"
            />
            <p className="text-xs text-muted-foreground">
              これらのパターンを含む部分を除外します
            </p>
          </div>
          
          {/* 創造性レベル */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="creativityLevel">創造性レベル</Label>
              <span className="text-sm text-muted-foreground">
                {input.creativityLevel || 5}/10
              </span>
            </div>
            <Slider
              id="creativityLevel"
              min={1}
              max={10}
              step={1}
              value={[input.creativityLevel || 5]}
              onValueChange={([value]) => onChange({ 
                ...input, 
                creativityLevel: value 
              })}
              className="w-full"
            />
            <p className="text-xs text-muted-foreground">
              低い値ほど原文に忠実、高い値ほど自由な要約になります
            </p>
          </div>
          
          {/* チャンク設定（大容量テキスト用） */}
          <div className="space-y-2">
            <Label>チャンク処理設定</Label>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label htmlFor="chunkSize" className="text-xs">
                  チャンクサイズ
                </Label>
                <Input
                  id="chunkSize"
                  type="number"
                  value={input.chunkSize || 100000}
                  onChange={(e) => onChange({
                    ...input,
                    chunkSize: parseInt(e.target.value) || undefined
                  })}
                  placeholder="100000"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="overlapSize" className="text-xs">
                  オーバーラップ
                </Label>
                <Input
                  id="overlapSize"
                  type="number"
                  value={input.overlapSize || 1000}
                  onChange={(e) => onChange({
                    ...input,
                    overlapSize: parseInt(e.target.value) || undefined
                  })}
                  placeholder="1000"
                />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              チャンクサイズ（デフォルト10万文字）を超えるテキストは自動的に分割処理されます
            </p>
          </div>
        </div>
      )}
    </div>
  )
}